import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnnualReportNavigationProvider, AnnualReportProvider } from '@modeso/aumico-lib-annualreport-fe';
import {
    AnnouncementsProvider,
    AumBreadcrumbComponentItem,
    DateLocalePipe,
    EMPTY_ARRAY_LENGTH,
    IPageHeader,
} from '@modeso/aumico-lib-core-fe';
import { CPMMState } from '@modeso/aumico-lib-users-fe/enums/cpmm-state.enum';
import {
    AnnualReportStep,
    IAnnualReport,
    IGeneralInformation,
    ReportType
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActionsSubject, Store } from '@ngrx/store';
import Debug from 'debug';
import { cloneDeep } from 'lodash-es';
import { filter } from 'rxjs';
import { find, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { AutomaticTransferOfAnnualResultService } from '../../../../../../aumico-lib-templates-fe/src/lib';
import {
    FCPState,
    GetOrganizationDto,
    OrganizationProvider,
} from '../../../../../../aumico-lib-users-fe/src/public-api';
import { Paths } from '../../../enums/paths.enum';
import { RouteLabels } from '../../../enums/routeLabels.enum';
import { NAVIGATION_TABS_MAP } from '../annual-report-navigation-tabs.constant';
const COMPANY_YEARS_SEPARATOR = ' - ';
const YEARS_SEPARATOR = '/';
const LAST_TWO_DIGITS_OF_YEAR = -2;
const debug = Debug('aumico:reports:fsEditPage');
@UntilDestroy()
@Component({
    selector: 'app-financial-statement-edit',
    template: `<app-aumico-layout *ngIf="!isCpmmActive || !isMandateAndTaskManagementEnabled || isInterimReport" [hideFooter]="false">
            <ng-container header>
                <ham-page-header
                    *ngIf="!isCpmmActive || !isMandateAndTaskManagementEnabled || isInterimReport"
                    [title]="pageHeader.value"
                    [breadcrumbs]="breadcrumbItems"
                    [backButtonRoute]="backButtonRoute"
                ></ham-page-header>
                <ham-page-tab-navigation
                    *ngIf="!isCpmmActive || !isMandateAndTaskManagementEnabled || isInterimReport"
                    [navigationItems]="activeNavigationTabs"
                    [mappingFCPVersionMismatch]="mappingFCPVersionMismatch"
                    [mappingCashflowVersionMismatch]="mappingCashflowVersionMismatch"
                ></ham-page-tab-navigation>
                <inner-page-tab-navigation
                    *ngIf="isCpmmActive && isMandateAndTaskManagementEnabled && !isInterimReport"
                    [navigationItems]="activeNavigationTabs"
                    [mappingFCPVersionMismatch]="mappingFCPVersionMismatch"
                    [mappingCashflowVersionMismatch]="mappingCashflowVersionMismatch"
                ></inner-page-tab-navigation>
            </ng-container>
            <ng-container content>
                <router-outlet></router-outlet>
            </ng-container>
        </app-aumico-layout>
        <div class="report-inner-tabs" *ngIf="isCpmmActive && isMandateAndTaskManagementEnabled && !isInterimReport">
            <ng-container header>
                <ham-page-header
                    *ngIf="!isCpmmActive || !isMandateAndTaskManagementEnabled || isInterimReport"
                    [title]="pageHeader.value"
                    [breadcrumbs]="breadcrumbItems"
                    [backButtonRoute]="backButtonRoute"
                ></ham-page-header>
                <ham-page-tab-navigation
                    *ngIf="!isCpmmActive || !isMandateAndTaskManagementEnabled || isInterimReport"
                    [navigationItems]="activeNavigationTabs"
                    [mappingFCPVersionMismatch]="mappingFCPVersionMismatch"
                    [mappingCashflowVersionMismatch]="mappingCashflowVersionMismatch"
                ></ham-page-tab-navigation>
                <inner-page-tab-navigation
                    *ngIf="isCpmmActive && isMandateAndTaskManagementEnabled && !isInterimReport"
                    [navigationItems]="activeNavigationTabs"
                    [mappingFCPVersionMismatch]="mappingFCPVersionMismatch"
                    [mappingCashflowVersionMismatch]="mappingCashflowVersionMismatch"
                ></inner-page-tab-navigation>
            </ng-container>
            <ng-container content>
                <router-outlet></router-outlet>
            </ng-container>
        </div>
        `,
})
export class FinancialStatementEditPage implements OnInit {
    //labels
    readonly generalInformationLabel = RouteLabels.FINANCIAL_STATEMENT_GENRAL_INFORMATION;
    readonly importLabel = RouteLabels.FINANCIAL_STATEMENT_IMPORT;
    readonly cfsLabel = RouteLabels.FINANCIAL_STATEMENT_CASH_FLOW;
    readonly notesLabel = RouteLabels.FINANCIAL_STATEMENT_NOTES_STEP;
    readonly aopLabel = RouteLabels.FINANCIAL_STATEMENT_AOP;
    readonly equityLabel = RouteLabels.FINANCIAL_STATEMENT_EQUITY_MOVEMENT_SCHEDULE;
    readonly fixedAssetsLabel = RouteLabels.FINANCIAL_STATEMENT_FIXED_ASSETS_SCHEDULE;
    readonly adjustmentsAndReclassesLabel = RouteLabels.FINANCIAL_STATEMENT_ADJUSTMENTS_AND_RECLASSES;
    readonly complementaryReportsLabel = RouteLabels.FINANCIAL_STATEMENT_COMPLEMENTARY_REPORTS;
    readonly printLabel = RouteLabels.FINANCIAL_STATEMENT_FINISH_AND_PRINT;
    readonly fcpLabel = RouteLabels.FINANCIAL_STATEMENT_CLOSING_PROCESS;

    //paths
    readonly generalInformationPath = Paths.GENERAL_INFORMATION;
    readonly importPath = Paths.IMPORT;
    readonly cfsPath = Paths.CFS;
    readonly notesPath = Paths.NOTES;
    readonly aopPath = Paths.AOP;
    readonly equityPath = Paths.EQUITY_MOVEMENT_SCHEDULE;
    readonly fixedAssetsPath = Paths.FAS;
    readonly adjustmentsAndReclassesPath = Paths.ADJUSTMENTS_AND_RECLASSES;
    readonly complementaryReportsPath = Paths.COMPLEMENTARY_REPORTS;
    readonly printPath = Paths.PRINT;
    readonly fcpPath = Paths.FINANCIAL_CLOSING_PROCESS;

    pageHeader: IPageHeader = {
        isLocalizedKey: true,
        value: RouteLabels.PROJECT_MAIN_NAVIGATION,
    };

    breadcrumbItems: AumBreadcrumbComponentItem[] = [
        {
            displayName: RouteLabels.PROJECT_MAIN_NAVIGATION,
            routerLink: '../',
        },
    ];

    activeNavigationTabs: {
        routerLink: string;
        tabLabelKey: string;
    }[] = [
            {
                routerLink: Paths.GENERAL_INFORMATION,
                tabLabelKey: RouteLabels.FINANCIAL_STATEMENT_GENRAL_INFORMATION,
            },
        ];

    backButtonRoute: string = '../overview';
    isBreadcrumbConstructed = false;

    private readonly balanceIndex = 0;
    mappingFCPVersionMismatch: boolean;
    mappingCashflowVersionMismatch: boolean;
    public isCpmmActive: boolean = false;
    public isMandateAndTaskManagementEnabled: boolean = false;
    public isInterimReport: boolean = false;

    private activeAnnualReport: IAnnualReport;
    private activeAnnualReportEncryptedId: string | undefined;


    constructor(
        private readonly providerOfAnnualReports: AnnualReportProvider,
        private readonly organizationProvider: OrganizationProvider,
        private annualReportNavigationProvider: AnnualReportNavigationProvider,
        private readonly announcementsProvider: AnnouncementsProvider,
        private route: ActivatedRoute,
        private router: Router,
        private store: Store,
        private dateLocalePipe: DateLocalePipe,
        private readonly automaticTransferOfAnnualResultService: AutomaticTransferOfAnnualResultService,
        private readonly actionListener$: ActionsSubject,

    ) { }

    ngOnInit(): void {
        this.announcementsProvider.dispatchGetUserAnnouncements();

        this.route.queryParams.subscribe((params) => {
            if (params['backTarget']) {
                this.backButtonRoute = params['backTarget'];
                debug('params', this.backButtonRoute);
            }
        });

        this.organizationProvider
            .getCurrentOrganization$()
            .pipe(
                filter((organization) => organization != null),
                take(1),
                untilDestroyed(this),
            )
            .subscribe((organization: GetOrganizationDto) => {
                this.isCpmmActive = organization.cpmmActivationDetails?.activationState === CPMMState.ACTIVE;
                this.isMandateAndTaskManagementEnabled = organization.isMandateAndTaskManagementEnabled ?? false;
            });

        this.providerOfAnnualReports
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                find((activeAnnualReportEncryptedId) => activeAnnualReportEncryptedId != null),
                untilDestroyed(this),
            )
            .subscribe(
                (activeAnnualReportEncryptedId) => (this.activeAnnualReportEncryptedId = activeAnnualReportEncryptedId),
            );



        this.providerOfAnnualReports
            .getActiveAnnualReport$()
            .pipe(
                tap(
                    (annualReport) =>
                    (this.isInterimReport =
                        annualReport?.setupData?.reportType == ReportType.QUARTERLY ||
                        annualReport?.setupData?.reportType == ReportType.MONTHLY ||
                        annualReport?.setupData?.reportType == ReportType.CUSTOM),
                ),
                filter(
                    (activeAnnual: IAnnualReport) => activeAnnual != null && activeAnnual.generalInformation != null,
                ),
                withLatestFrom(this.organizationProvider.getCurrentOrganization$()),
                untilDestroyed(this),
            )
            .subscribe(([annualReport, organization]) => {
                this.activeAnnualReport = cloneDeep<IAnnualReport>(annualReport);
                const isFcpActive = [FCPState.PAID, FCPState.TRIAL].includes(
                    organization?.fcpActivationDetails?.subscriptionState,
                );

                this.checkChangeInMappingHappened(annualReport, isFcpActive);

                if (!this.isBreadcrumbConstructed) {
                    this.handleChangePageHeaderForAnnualReport(annualReport);
                }

            });


        this.annualReportNavigationProvider
            .getActiveArNavigation$()
            .pipe(
                map((arNavigations) => {
                    return arNavigations.filter(
                        (nav) =>
                            nav !== AnnualReportStep.BALANCE_ACCOUNT_PROOF &&
                            nav !== AnnualReportStep.INCOME_ACCOUNT_PROOF,
                    );
                }),

                withLatestFrom<AnnualReportStep[], [boolean]>(this.providerOfAnnualReports.getAnnualReportIsSealed$()),
                untilDestroyed(this),
            )
            .subscribe(([navigation, isSealed]: [AnnualReportStep[], boolean]) => {
                if (isSealed) {
                    this.handleIsSealed(navigation);
                } else {
                    this.handleNavigationTabs(navigation);
                }
            });

        //TODO implement a proper way to handle the case when the user navigates to the edit page without an active annual report using ui navigation states
    }

    private checkChangeInMappingHappened(annualReport: IAnnualReport, isFcpActive: boolean): void {
        if (annualReport != null && annualReport.generalInformation != null && annualReport.finStatements.length > 0) {
            const mappingVersion = annualReport.finStatements[this.balanceIndex].version;
            const { fcp, cashFlowStatement } = annualReport;
            if (isFcpActive && fcp) {
                this.mappingFCPVersionMismatch = fcp.version !== mappingVersion;
                if (!this.mappingFCPVersionMismatch) {
                    this.mappingCashflowVersionMismatch =
                        cashFlowStatement != null && cashFlowStatement.version !== fcp.version;
                }
            } else {
                this.mappingCashflowVersionMismatch =
                    cashFlowStatement != null && cashFlowStatement.version !== mappingVersion;
            }
        }
    }

    private handleNavigationTabs(navigation: AnnualReportStep[]): void {
        if (navigation.length === EMPTY_ARRAY_LENGTH) {
            this.activeNavigationTabs = [];
        }
        this.activeNavigationTabs = [
            {
                routerLink: Paths.GENERAL_INFORMATION,
                tabLabelKey: RouteLabels.FINANCIAL_STATEMENT_GENRAL_INFORMATION,
            },
        ];
        const annualReportTabs: AnnualReportStep[] = cloneDeep<AnnualReportStep[]>(navigation);
        // to remove the general information step since it is already displayed
        if (annualReportTabs[0] === AnnualReportStep.GENERAL_INFORMATION) {
            annualReportTabs.shift();
        }
        annualReportTabs.forEach((tab) => {
            const navDetails = NAVIGATION_TABS_MAP.get(tab);
            this.activeNavigationTabs.push(navDetails);
        });
    }

    private handleChangePageHeaderForAnnualReport(annualReport: IAnnualReport): void {
        if (annualReport == null || annualReport.generalInformation == null) {
            return;
        }
        const constructedAnnualReportPageHeader = this.constructAnnualReportPageHeader(annualReport.generalInformation);

        this.pageHeader = {
            isLocalizedKey: false,
            value: constructedAnnualReportPageHeader,
        };

        this.breadcrumbItems.push({ displayName: constructedAnnualReportPageHeader, isActive: true });
        this.isBreadcrumbConstructed = true;
    }

    private constructAnnualReportPageHeader(generalInformation: IGeneralInformation): string {
        const companyName = generalInformation.company.label ? generalInformation.company.name + ' (' + generalInformation.company.label + ')' : generalInformation.company.name;

        if (this.isInterimReport) {
            const startDate = this.dateLocalePipe.transform(generalInformation.incomeCurrentYearFromDate, undefined, undefined, false, false).toString();
            const endDate = this.dateLocalePipe.transform(generalInformation.incomeCurrentYearToDate, undefined, undefined, false, false).toString();

            return (
                companyName +
                COMPANY_YEARS_SEPARATOR +
                startDate + COMPANY_YEARS_SEPARATOR +
                endDate
            );
        } else {
            return (
                companyName +
                COMPANY_YEARS_SEPARATOR +
                this.getYearFromDate(generalInformation.balanceSheetDate, generalInformation.balancePreviousYearDate)
            );
        }

    }

    private getYearFromDate(balanceSheetDate: string, balancePreviousYearDate: string | null): string {
        const balanceSheetCurrentYear = new Date(balanceSheetDate).getFullYear().toString();

        if (balancePreviousYearDate == null) {
            const balanceSheetDateObj = new Date(balanceSheetDate);
            balancePreviousYearDate = new Date(
                balanceSheetDateObj.setFullYear(balanceSheetDateObj.getFullYear() - 1),
            ).toISOString();
        }
        const balanceSheetPreviousYear = new Date(balancePreviousYearDate).getFullYear().toString();

        if (balanceSheetCurrentYear === balanceSheetPreviousYear) {
            return balanceSheetCurrentYear;
        }
        const balanceSheetCurrentYearShortened = balanceSheetCurrentYear.slice(LAST_TWO_DIGITS_OF_YEAR);
        const balanceSheetPreviousYearShortened = balanceSheetPreviousYear.slice(LAST_TWO_DIGITS_OF_YEAR);

        return balanceSheetCurrentYearShortened + YEARS_SEPARATOR + balanceSheetPreviousYearShortened;
    }

    private handleIsSealed(navigation: AnnualReportStep[]): void {
        // const printTabOnly = navigation[0];
        const navDetails = NAVIGATION_TABS_MAP.get(AnnualReportStep.PRINT);
        this.activeNavigationTabs = [navDetails];

        // Redirect to print page if not already there
        if (!this.router.url.includes(Paths.PRINT)) {
            const currentUrl = this.router.url;
            const baseUrl = currentUrl.substring(0, currentUrl.lastIndexOf('/'));
            const backTarget = new URL(this.router.url, 'http://x').searchParams.get('backTarget');
            const queryString = backTarget ? `?backTarget=${encodeURIComponent(backTarget)}` : '';
            this.router.navigateByUrl(`${baseUrl}/${Paths.PRINT}${queryString}`);
        }
    }

    goBack() {
        if (this.backButtonRoute) {
            this.router.navigateByUrl(decodeURIComponent(this.backButtonRoute));
        } else {
            // Fallback if backButtonRoute is not set
            this.router.navigate(['../overview']);
        }
    }
}
